import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, Loader2, LogOut } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Dedicated admin login gate for /admin — authenticates against the seeded
// ADMIN_EMAIL/ADMIN_PASSWORD account and denies non-admin users.
export function AdminLogin({ deniedUser }) {
  const { login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        toast.error("This account does not have admin access.");
        await logout();
      } else {
        toast.success("Welcome, admin");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="admin-login">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glass-strong rounded-3xl p-8 w-full max-w-md border-white/10">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-[0_0_24px_rgba(59,130,246,0.45)] mb-4">
            <ShieldAlert className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-display font-black text-2xl tracking-tight">Admin Portal</h1>
          <p className="text-white/50 text-sm mt-1">Restricted access — administrators only.</p>
        </div>

        {deniedUser && deniedUser.role !== "admin" && (
          <div data-testid="admin-denied-banner" className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            You're signed in as <span className="font-semibold">{deniedUser.email}</span>, which isn't an admin account. Sign in with admin credentials below.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-white/70 text-sm">Admin email</Label>
            <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
          </div>
          <div>
            <Label className="text-white/70 text-sm">Admin password</Label>
            <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="off" className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
          </div>
          <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full rounded-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 active:scale-95 transition-all font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter admin portal"}
          </Button>
        </form>

        {deniedUser && (
          <button data-testid="admin-logout-link" onClick={logout} className="mt-5 mx-auto flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Log out of {deniedUser.email}
          </button>
        )}
      </motion.div>
    </div>
  );
}
